import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Portal, Select, Tabs, createListCollection } from '@chakra-ui/react';
import {
  LuSettings,
  LuUsers,
  LuLogOut,
  LuGamepad2,
  LuLayoutDashboard,
  LuMegaphone,
  LuNetwork,
  LuPencilRuler,
  LuZap,
  LuCheck,
  LuChevronDown,
} from 'react-icons/lu';
import { useAuth } from '../contexts/AuthContext';
import { getActiveNavigationValue } from '../utils/navigation.js';

const NAV_TABS = [
  { value: '/overview', label: 'Overview', icon: LuLayoutDashboard, disabled: true },
  {
    value: '/creative-manager',
    label: 'Creative Manager',
    icon: LuPencilRuler,
    requires: 'creatives.view',
  },
  {
    value: '/campaigns',
    label: 'Campaigns',
    icon: LuMegaphone,
    requires: 'campaigns.view',
  },
  {
    value: '/playable-manager',
    label: 'Playable Manager',
    icon: LuGamepad2,
    requires: 'playables.view',
  },
  {
    value: '/automations',
    label: 'Automations',
    icon: LuZap,
    requires: 'automations.view',
  },
  {
    value: '/uao/networks',
    label: 'UAO Networks',
    icon: LuNetwork,
    requires: 'uao.view',
  },
];

export default function Navbar({
  applications,
  hideApplicationSelector = false,
  selectedProject,
  onChangeProject,
}) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const availableTabs = NAV_TABS.filter((tab) => !tab.requires || hasPermission(tab.requires));
  const currentTab = getActiveNavigationValue(location.pathname, NAV_TABS);
  const canViewUserManagement = hasPermission('users.view');
  const activeTabRef = useRef(null);
  const projectOptions = useMemo(
    () =>
      createListCollection({
        items: applications,
        itemToValue: (application) => application.uuid,
        itemToString: (application) => application.name,
      }),
    [applications],
  );

  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [currentTab]);

  const handleLogout = async () => {
    setLogoutError(false);
    setLoggingOut(true);
    try {
      await logout();
      setIsUserMenuOpen(false);
    } catch {
      setLogoutError(true);
    } finally {
      setLoggingOut(false);
    }
  };

  const handleSettings = () => {
    navigate('/settings');
    setIsUserMenuOpen(false);
  };

  const handleUserManagement = () => {
    navigate('/user-management');
    setIsUserMenuOpen(false);
  };

  return (
    <div className="navbar">
      {!hideApplicationSelector && (
        <Select.Root
          unstyled
          className="app-selector"
          collection={projectOptions}
          value={selectedProject ? [selectedProject] : []}
          onValueChange={({ value }) => {
            if (value[0]) onChangeProject(value[0]);
          }}
          positioning={{ strategy: 'fixed', sameWidth: true }}
        >
          <Select.HiddenSelect aria-hidden="true" tabIndex={-1} />
          <Select.Control>
            <Select.Trigger className="app-selector-trigger" aria-label="Project">
              <Select.ValueText placeholder="Select project" />
              <Select.Indicator>
                <LuChevronDown aria-hidden="true" />
              </Select.Indicator>
            </Select.Trigger>
          </Select.Control>
          <Portal>
            <Select.Positioner style={{ zIndex: 1500 }}>
              <Select.Content className="app-project-menu" aria-label="Projects">
                {applications.map((application) => (
                  <Select.Item
                    key={application.uuid}
                    item={application}
                    className="app-project-option"
                  >
                    <Select.ItemText>{application.name}</Select.ItemText>
                    <Select.ItemIndicator>
                      <LuCheck aria-hidden="true" />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      )}

      <div className="nav-tabs">
        <Tabs.Root
          value={currentTab}
          onValueChange={(details) => navigate(details.value)}
          variant="plain"
          size="sm"
        >
          <Tabs.List
            aria-label="Application navigation"
            css={{
              gap: '4px',
              border: 'none',
              background: 'transparent',
            }}
          >
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tabs.Trigger
                  ref={tab.value === currentTab ? activeTabRef : undefined}
                  key={tab.value}
                  value={tab.value}
                  disabled={tab.disabled}
                  css={{
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#94a3b8',
                    gap: '6px',
                    cursor: tab.disabled ? 'default' : 'pointer',
                    whiteSpace: 'nowrap',
                    borderRadius: '6px',
                    border: 'none',
                    background: 'transparent',
                    transition: 'all 0.15s ease',
                    opacity: tab.disabled ? 0.5 : 1,
                    _selected: {
                      color: '#234e52',
                      background: '#e6fffa',
                      fontWeight: 600,
                    },
                    _hover: tab.disabled
                      ? {}
                      : {
                          color: '#4a5568',
                          background: '#f7fafc',
                        },
                  }}
                >
                  <Icon size={14} />
                  {tab.label}
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>
        </Tabs.Root>
      </div>

      <div className="user-section">
        <Menu.Root
          onOpenChange={({ open }) => setIsUserMenuOpen(open)}
          positioning={{ placement: 'bottom-end', gutter: 4 }}
          lazyMount
          unmountOnExit
        >
          <Menu.Trigger asChild>
            <button
              className="user-button"
              aria-label={`User menu for ${user?.name || user?.email || 'User'}`}
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="user-avatar"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="user-icon" aria-hidden="true">
                  {(user?.name || user?.email || 'U')
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join('')
                    .toUpperCase()}
                </span>
              )}
              <span className="user-name">{user?.name || user?.email || 'User'}</span>
              <span className={`dropdown-arrow ${isUserMenuOpen ? 'open' : ''}`} aria-hidden="true">
                ▼
              </span>
            </button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner style={{ zIndex: 1100 }}>
              <Menu.Content
                className={`user-dropdown${currentTab === '/creative-manager' ? ' cm-user-menu' : ''}`}
                aria-label="User menu"
              >
                <div className="user-info">
                  <strong>{user?.name || 'User'}</strong>
                  <p className="user-email">{user?.email}</p>
                </div>
                <Menu.Item value="settings" className="dropdown-item" onSelect={handleSettings}>
                  <span className="dropdown-icon" aria-hidden="true">
                    <LuSettings />
                  </span>
                  Settings
                </Menu.Item>
                {canViewUserManagement && (
                  <Menu.Item
                    value="users"
                    className="dropdown-item"
                    onSelect={handleUserManagement}
                  >
                    <span className="dropdown-icon" aria-hidden="true">
                      <LuUsers />
                    </span>
                    User Management
                  </Menu.Item>
                )}
                <Menu.Item
                  value="logout"
                  className="dropdown-item"
                  onSelect={handleLogout}
                  closeOnSelect={false}
                  disabled={loggingOut}
                >
                  <span className="dropdown-icon" aria-hidden="true">
                    <LuLogOut />
                  </span>
                  {loggingOut ? 'Logging out…' : 'Logout'}
                </Menu.Item>
                {logoutError && (
                  <p role="alert" className="logout-error">
                    Could not log out. Please try Logout again.
                  </p>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </div>
    </div>
  );
}
